/**
 * Drives a layout to its answer a frame at a time, so arriving at one is watchable rather than a
 * freeze.
 *
 * The two force-based arrangements take seconds at a few hundred marks - around 2.1s at 754 - and
 * run to completion in one call they hold the main thread for all of it: no frame is presented, no
 * pointer event is handled, and the pane is blank until the whole solve is done. Sliced instead,
 * the same work is spread over frames and the reader watches the graph relax, which is how a force
 * layout is conventionally shown and tells them something a progress bar cannot: which clusters
 * were together from the start.
 *
 * The three closed-form arrangements arrive settled, so this costs them one render and nothing
 * else.
 */

import { useEffect, useMemo, useState } from 'react';
import type { NetworkLayout } from '../types';
import { prefersReducedMotion } from '../../reducedMotion';
import {
  createNetworkLayoutSolve,
  type BuildNetworkLayoutArgs,
} from './buildNetworkLayout';

/**
 * How long a batch of iterations may take, in milliseconds.
 *
 * A time budget rather than a count of steps, because a step is not a fixed cost: it is about half
 * a millisecond at a hundred marks and nearly seven at seven hundred, so any fixed count is either
 * a stutter on a large estate or a needlessly long settle on a small one. Spending a budget leaves
 * the frame rate roughly constant and lets the number of iterations per frame fall out of it.
 *
 * Under a frame at 60Hz, with room left for the draw the settling is competing with - the view
 * repacks its instance buffers for every intermediate arrangement, and a budget that filled the
 * frame would starve the thing the reader is actually looking at.
 */
const SETTLING_BUDGET = 8;

/**
 * The arrangement as it stands, re-rendered while it settles.
 *
 * Takes the same arguments as `buildNetworkLayout`, so a caller swaps one for the other.
 */
export const useSettlingLayout = (
  args: BuildNetworkLayoutArgs
): NetworkLayout => {
  const { graph, kind, seed, iterations, bundleStrength, zStretch, nodeSizes } =
    args;

  /* Keyed on the same inputs the one-shot memo was, so a settling solve is abandoned exactly when
     the arrangement it was arriving at stopped being the one wanted. */
  const solve = useMemo(() => {
    const created = createNetworkLayoutSolve({
      graph,
      kind,
      seed,
      iterations,
      bundleStrength,
      zStretch,
      nodeSizes,
    });
    /* A reader who has asked for less motion gets the arrangement rather than the arriving at it.
       Settled here rather than by cutting the animation short below, so it is already done by the
       time there is anything to show and no intermediate frame is ever presented. The cost is the
       freeze this hook exists to remove, which is the trade they have asked for - and it is what
       `useLayoutTransition` does with the same preference. */
    if (prefersReducedMotion()) created.advance(created.remaining);
    return created;
  }, [graph, kind, seed, iterations, bundleStrength, zStretch, nodeSizes]);

  const [layout, setLayout] = useState(() => solve.toLayout());

  /**
   * Reset during render rather than in an effect, which is React's own guidance for state derived
   * from a prop - an effect would let one frame paint the previous arrangement's positions under
   * the new graph's nodes, which is the stale-index failure the hover reset exists to avoid.
   */
  const [shown, setShown] = useState(solve);
  const isNewSolve = shown !== solve;
  /* Answered from the new solve in this pass, not from the state the re-render will carry.
     `setState` during render does not end the render: the body runs to completion, and what it
     hands downstream on the way is kept even though the output is thrown away. */
  const current = isNewSolve ? solve.toLayout() : layout;
  if (isNewSolve) {
    setShown(solve);
    setLayout(current);
  }

  useEffect(() => {
    if (solve.remaining === 0) return;

    let frame: number | undefined = requestAnimationFrame(function tick() {
      const until = performance.now() + SETTLING_BUDGET;
      /* At least one iteration a frame whatever the budget says. A single step can cost more than
         the whole budget on a large estate, and a loop that checked the clock first would then
         never advance - a settle that never finishes is worse than a slow one. */
      do {
        solve.advance(1);
      } while (solve.remaining > 0 && performance.now() < until);

      setLayout(solve.toLayout());
      frame = solve.remaining > 0 ? requestAnimationFrame(tick) : undefined;
    });

    return () => {
      if (frame !== undefined) cancelAnimationFrame(frame);
    };
  }, [solve]);

  return current;
};
